import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import {
  canonicalizeGrokMediaModelId,
  getGrokMediaModelContract,
  type EffortLevel,
  reconcileCurationAgainstCatalog,
  stripModelIdTagSuffix,
  resolveCanonicalGrokMediaCatalog,
  type ModelCatalogEntry,
  type PlatformMediaGenerationModelCuration,
  type PlatformModelDefaultParams,
  type PlatformToolCuration as CurationData,
  type PlatformToolCurationViolation as Violation,
  type AudioTranscribeProviderId,
  type ToolAdmissionRejection,
  type WebSearchProviderId,
} from "@dosia/shared";
import { apiCall } from "../lib/api-client.js";
import { ApiError } from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { useApiQuery } from "../lib/useApiQuery.js";
import { usePlatformModelCatalog } from "../lib/usePlatformModelCatalog.js";
import { platformToolCurationPath } from "../lib/api-paths.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { cn } from "../lib/cn.js";
import { PrimaryButton, SecondaryButton, Select, TextInput } from "../components/FormField.js";
import { ProviderChainCard } from "./platform-tool-curation/ProviderChainCard.js";
import { ToolCard } from "./platform-tool-curation/ToolCard.js";
import { IsolatedDraftInput } from "./platform-tool-curation/IsolatedDraftInput.js";
import {
  bucketCandidatesFromIndex,
  buildCatalogIndex,
  defaultGroupIndexed,
  enabledIdsOf,
  formatProtocolLabel,
  imageGenCandidatesFromIndex,
  listAudioTranscribeProviders,
  listBucketModels,
  listWebSearchProviders,
  moveIndex,
  toggleListed,
  videoGenCandidatesFromIndex,
  videoGenFlagsFromIndex,
  workModelCandidatesFromIndex,
  type CatalogIndex,
  type ListedModel,
  type VideoGenFlags,
} from "../lib/platform-tool-curation-view.js";

/** 内容不变就归还上一份引用，避免「每键一次新数组」把下游 useMemo 打穿。 */
function useStableStringList(ids: readonly string[]): readonly string[] {
  const ref = useRef<readonly string[]>(ids);
  const prev = ref.current;
  if (prev.length === ids.length) {
    let same = true;
    for (let i = 0; i < ids.length; i++) {
      if (prev[i] !== ids[i]) {
        same = false;
        break;
      }
    }
    if (same) return prev;
  }
  ref.current = ids;
  return ids;
}

// baseId 的权威源在 @dosia/shared（stripModelIdTagSuffix）——本页原有的本地副本已退役。
const baseId = stripModelIdTagSuffix;

/**
 * 模型选择器：**只能从网关候选里选**（2026-08-30 用户拍板「超管只能选里面有的」）。
 *
 * 此前是 `<TextInput list=...>`（datalist 只是建议，可以随手打任意字符串），于是一个网关
 * 根本没有的 id 能一路存进 Mongo、fan-out 到全员，全程零报错，只在客户端被静默跳过。
 *
 * ## 已保存但已下架的值必须仍在选项里
 *
 * 这是本组件唯一不平凡的地方：`<select>` 的 value 若不在 options 里，浏览器会渲染成空/首项，
 * 超管一按保存就**静默把配置改成了别的模型**——比原来的自由输入还糟。所以当前值恒作为一个
 * 选项存在，只是标注「⚠️ 已下架」：看得见、保存不变形、但**新选不中**（它不在候选里，
 * 选走了就回不来）。
 *
 * 服务端另有一道闸（`checkCurationAgainstLiveCatalog`）——绕过 UI 直调 API 同样拦得住。
 * 本组件是可用性的一半，不是安全的那一半。
 */
function ModelSelect({
  value,
  candidates,
  onChange,
  ariaLabel,
  placeholder,
  className,
}: {
  value: string;
  candidates: readonly string[];
  onChange: (next: string) => void;
  ariaLabel: string;
  placeholder: string;
  className?: string;
}): JSX.Element {
  const current = value.trim();
  const canonical = current ? canonicalizeGrokMediaModelId(current) : undefined;

  // **两件不同的事，判据不同，别合成一个 flag**（2026-08-30 合成过一次，测试当场抓住）：
  //
  // ① `needsOwnOption` —— 当前值在 options 里没有对应项时**必须补一个**，否则 `<select>`
  //    渲染成空、超管一按保存就把配置静默改成了别的模型。判据只看**字面**在不在候选里，
  //    因为 option 的 value 是字面量。
  // ② `retired` —— 是否标「已下架」。判据要**两种写法都比**：候选集里的 Grok 媒体行是
  //    归一后的 canonical（见 imageGenCandidates），而这里拿到的是策展原样存储的值。
  //    只比字面 → 策展写别名（`grok-imagine`）而清单里是 canonical 时会被误标已下架，
  //    可它运行时完全可用。这与 reconcileCurationAgainstCatalog 里修过的是同一类假阳，
  //    而假阳出现在「专门用来暴露失效配置」的标记上最坏：超管会以为好行是坏的然后去改它。
  //
  // 合成一个 flag 的后果正好是两种错各占一半：别名行既丢了 option（值被清空）、又不标红。
  const needsOwnOption = current.length > 0 && !candidates.includes(current);
  const retired = needsOwnOption && !(canonical && candidates.includes(canonical));
  return (
    <Select
      value={current}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={cn(className, retired && "border-dosia-danger/60 text-dosia-danger")}
    >
      <option value="">{placeholder}</option>
      {needsOwnOption && (
        <option value={current}>
          {retired ? `${current}（⚠️ 已下架，网关清单里已无此模型）` : current}
        </option>
      )}
      {candidates.map((id) => (
        <option key={id} value={id}>
          {id}
        </option>
      ))}
    </Select>
  );
}

// CurationData 与 Violation 契约来自 @dosia/shared 单一真相源（#1820 已落，红线#9）：
// 前端本地校验 + 后端 400 回报共用同一 PlatformToolCurationViolation 形状。

/**
 * 前端生图链校验（`validatePlatformToolCuration` 的镜像；服务端仍是权威，本地只为
 * 「保存前即时红框 + 禁存」的体验，红线 §5：用户错误输入 → 红框合理）。生图段 simplify
 * 2026-07-23：exposedId 假名与 multiRowChains 开关退役后，链键=modelId，只剩规则①：
 *  ① (modelId, group) 全局判重——同一「模型+凭证组」组合只能出现一次；同 modelId 挂不同
 *     group 的多行 = 合法分组优先级链，不判重。
 * rowIndex 对齐 imageGenModels 扁平数组下标（= 服务端所见），供 UI 按行定位。
 */
function validateMediaGenChains(
  rows: Array<{ modelId: string; group: string }>,
  field = "group",
): Violation[] {
  const out: Violation[] = [];
  const norm = (s: string): string => s.trim();

  const seenModelGroup = new Map<string, number>();
  rows.forEach((r, i) => {
    const mid = norm(r.modelId);
    const grp = norm(r.group);
    if (!mid || !grp) return;
    const mg = `${mid}␟${grp}`;
    const prev = seenModelGroup.get(mg);
    if (prev !== undefined) {
      out.push({
        rowIndex: i,
        field,
        rule: "duplicate-model-group",
        message: `与第 ${prev + 1} 行的（模型 ${mid} · 组 ${grp}）重复，同一模型+组只能出现一次`,
      });
    } else {
      seenModelGroup.set(mg, i);
    }
  });

  return out;
}

/**
 * L1.5 平台策展-工具模型清单（PR-T，super-admin 平台面）。
 *
 * 业务：视觉理解 / 读 PDF / 视频理解 / 生图 / 视频生成的工具模型由超管在此配置一份有序清单
 * （全平台生效）。**留空 = 未配置 = 该工具不可用**（主脑看不到它，也就不会拿它去试）。红线 §5：
 * 非 bug 不红色，说明走 amber info，红色只留保存失败。
 */
type VisionRow = { modelId: string; scenario: string };
/**
 * per-行参数字段（unsupportedParams/paramSchema/paramDefaults，见 shared types/index.ts
 * PlatformToolCuration.imageGenModels）——红线 #9 单一真相源：这些字段必须随行 load/save 全字段
 * 往返，不能只搬 {modelId,group,scenario} 三件套（否则编辑任意一行、保存时会把全部行的这些字段
 * 静默抹掉）。unsupportedParams 编辑态用逗号分隔文本（同 fallbackChain 输入模式）；paramSchema
 * 结构较深，本期仅原样透传，不做编辑 UI（见 PlatformToolCuration.tsx 顶部注释）。
 */
type ImageGenRow = {
  /** 客户端稳定 id——分组渲染打乱扁平下标后，用它做 React key + 组内移动锚点（非持久，不入 save body）。 */
  uid: string;
  modelId: string;
  group: string;
  scenario: string;
  unsupportedParams: string;
  paramSchema?: Record<string, string[]>;
  /** per-模型参数默认值（param→默认值，2026-07-23 生图段重做）。**卡级共享字段**（链内每行同值，
   *  同 modelId），编辑经 setChainParamDefault 同步组内每行；consumer=core handler 真注入。 */
  paramDefaults?: Record<string, string>;
};

type VideoGenRow = PlatformMediaGenerationModelCuration & { uid: string };

let imageGenRowSeq = 0;
const nextRowUid = (): string => `igr_${imageGenRowSeq++}`;
let videoGenRowSeq = 0;
const nextVideoRowUid = (): string => `vgr_${videoGenRowSeq++}`;

/** load/re-hydrate 单一实现——GET 初始 hydrate 与保存后用服务端回包重 hydrate 共用，
 *  避免两处手搓字段列表各写一份（红线 #9）。 */
function toImageGenRow(v: {
  modelId: string;
  group: string;
  scenario: string;
  unsupportedParams?: string[];
  paramSchema?: Record<string, string[]>;
  paramDefaults?: Record<string, string>;
}): ImageGenRow {
  const verifiedSchema = getGrokMediaModelContract(v.modelId)?.paramSchema;
  return {
    uid: nextRowUid(),
    modelId: v.modelId,
    group: v.group,
    scenario: v.scenario,
    unsupportedParams: (v.unsupportedParams ?? []).join(", "),
    paramSchema: v.paramSchema ?? verifiedSchema,
    paramDefaults: v.paramDefaults,
  };
}

function toVideoGenRow(v: PlatformMediaGenerationModelCuration): VideoGenRow {
  return {
    ...v,
    paramSchema: v.paramSchema ?? getGrokMediaModelContract(v.modelId)?.paramSchema,
    uid: nextVideoRowUid(),
  };
}

function narrowParamDefaults(
  defaults: Record<string, string> | undefined,
  schema: Record<string, string[]> | undefined,
): Record<string, string> | undefined {
  if (!defaults || !schema) return undefined;
  const narrowed = Object.fromEntries(
    Object.entries(defaults).filter(([param, value]) => schema[param]?.includes(value)),
  );
  return Object.keys(narrowed).length > 0 ? narrowed : undefined;
}

/** 生图条目卡分组结果——按 modelId 把扁平行聚合，保序（首次出现顺序）。同 modelId 多行 = 分组
 *  优先级链，聚为一卡；空行（无 modelId）各自成独立卡（合成键），点「添加生图模型」得到一张空卡。 */
type ImageGenChainGroup = { key: string; displayName: string; rows: Array<{ row: ImageGenRow; index: number }> };
function groupImageGenChains(rows: ImageGenRow[]): ImageGenChainGroup[] {
  const order: string[] = [];
  const map = new Map<string, ImageGenChainGroup>();
  rows.forEach((row, index) => {
    const chainKey = row.modelId.trim();
    const mapKey = chainKey || `__empty_${row.uid}`;
    let g = map.get(mapKey);
    if (!g) {
      g = { key: chainKey, displayName: chainKey || "（未命名模型）", rows: [] };
      map.set(mapKey, g);
      order.push(mapKey);
    }
    g.rows.push({ row, index });
  });
  return order.flatMap((k) => {
    const g = map.get(k);
    return g ? [g] : [];
  });
}

type WebSearchProviderRow = { provider: WebSearchProviderId; group: string };

type AudioTranscribeProviderRow = { provider: AudioTranscribeProviderId; group: string };

export function PlatformToolCuration(): JSX.Element {
  const { t } = useTranslation();
  const token = useAtomValue(authAtom)?.token;
  const curationQ = useApiQuery<{ curation: CurationData | null }>(
    platformToolCurationPath(),
    [],
  );
  const { entries: catalogEntries, source: catalogSource, loading: catalogLoading } = usePlatformModelCatalog();
  /**
   * 「已存策展」灌进 state 之前不渲染编辑区。空桶默认全选（`take*` / `*DisplayGroups`
   * 的合成行）只有在**确认这个桶没配过**之后才成立；GET 还在飞的时候渲染它，超管看到的
   * 是一屏「全勾上」的假象——既可能被误读成已保存的配置，也可能在这个窗口里点保存把它
   * 写进库。
   *
   * 判据必须是「hydrate 副作用已经跑完」，不是 `loading`、也不是 `curationQ.data !== null`：
   * - `loading` 初值 false，第一帧就漏过去；
   * - `data` 变非空的那一帧，hydrate 的 useEffect 还没执行，state 仍是空桶——正好又渲染
   *   一帧默认全选。这一帧的 DOM 节点随后被替换掉，看不见但真实存在（2026-09-07 的
   *   PlatformToolCuration 测试就是抓在这一帧上：拿到的是已脱离文档的旧节点）。
   */
  const [curationHydrated, setCurationHydrated] = useState(false);
  const curationLoaded = curationHydrated || curationQ.error !== null;

  const [vision, setVision] = useState<VisionRow[]>([]);
  const [documents, setDocuments] = useState<VisionRow[]>([]);
  const [videoUnderstand, setVideoUnderstand] = useState<VisionRow[]>([]);
  const [imageGen, setImageGen] = useState<ImageGenRow[]>([]);
  const [videoGen, setVideoGen] = useState<VideoGenRow[]>([]);
  const [fallbackChain, setFallbackChain] = useState("");
  const [modelCallModels, setModelCallModels] = useState<string[]>([]);
  const [modelParams, setModelParams] = useState<Record<string, PlatformModelDefaultParams>>({});
  const [webSearchProviders, setWebSearchProviders] = useState<WebSearchProviderRow[]>([]);
  const [audioTranscribeProviders, setAudioTranscribeProviders] = useState<AudioTranscribeProviderRow[]>([]);
  const [xSearchEnabled, setXSearchEnabled] = useState(false);
  const [xSearchGroup, setXSearchGroup] = useState("");
  const [openParamRow, setOpenParamRow] = useState<string | null>(null);
  const [openImageParamCard, setOpenImageParamCard] = useState<string | null>(null);
  const [videoModeByRow, setVideoModeByRow] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [serverViolations, setServerViolations] = useState<Violation[]>([]);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [visionTouched, setVisionTouched] = useState(false);
  const [documentTouched, setDocumentTouched] = useState(false);
  const [videoUnderstandTouched, setVideoUnderstandTouched] = useState(false);
  const [imageGenTouched, setImageGenTouched] = useState(false);
  const [videoGenTouched, setVideoGenTouched] = useState(false);
  const [workTouched, setWorkTouched] = useState(false);
  const [webSearchTouched, setWebSearchTouched] = useState(false);
  const [audioTranscribeTouched, setAudioTranscribeTouched] = useState(false);

  const visionRef = useRef<VisionRow[]>([]);
  const documentsRef = useRef<VisionRow[]>([]);
  const videoUnderstandRef = useRef<VisionRow[]>([]);
  const imageGenRef = useRef<ImageGenRow[]>([]);
  const videoGenRef = useRef<VideoGenRow[]>([]);
  const webSearchRef = useRef<WebSearchProviderRow[]>([]);
  const audioTranscribeRef = useRef<AudioTranscribeProviderRow[]>([]);
  const fallbackRef = useRef("");
  const xSearchGroupRef = useRef("");

  useEffect(() => {
    if (!curationQ.data) return;
    const c = curationQ.data.curation;
    const nextVision = c?.visionModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
    visionRef.current = nextVision;
    setVision(nextVision);
    const nextDocs = c?.documentModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
    documentsRef.current = nextDocs;
    setDocuments(nextDocs);
    const nextVu = c?.videoModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
    videoUnderstandRef.current = nextVu;
    setVideoUnderstand(nextVu);
    const nextImage = c?.imageGenModels?.map(toImageGenRow) ?? [];
    imageGenRef.current = nextImage;
    setImageGen(nextImage);
    const nextVideo = c?.videoGenModels?.map(toVideoGenRow) ?? [];
    videoGenRef.current = nextVideo;
    setVideoGen(nextVideo);
    const nextFallback = (c?.imageFallbackChain ?? []).join(", ");
    fallbackRef.current = nextFallback;
    setFallbackChain(nextFallback);
    setModelCallModels(c?.modelCallModels ?? []);
    setModelParams(c?.modelParams ?? {});
    const nextWeb = c?.webSearchProviders?.length
      ? c.webSearchProviders.map((p) => ({
          provider: p.provider,
          group: p.group ?? "",
        }))
      : [];
    webSearchRef.current = nextWeb;
    setWebSearchProviders(nextWeb);
    const nextAudio = c?.audioTranscribeProviders?.length
      ? c.audioTranscribeProviders.map((p) => ({
          provider: p.provider,
          group: p.group ?? "",
        }))
      : [];
    audioTranscribeRef.current = nextAudio;
    setAudioTranscribeProviders(nextAudio);
    setXSearchEnabled(c?.xSearch?.enabled === true);
    const nextX = c?.xSearch?.group ?? "";
    xSearchGroupRef.current = nextX;
    setXSearchGroup(nextX);
    setVisionTouched(false);
    setDocumentTouched(false);
    setVideoUnderstandTouched(false);
    setImageGenTouched(false);
    setVideoGenTouched(false);
    setWorkTouched(false);
    setWebSearchTouched(false);
    setAudioTranscribeTouched(false);
    setCurationHydrated(true);
  }, [curationQ.data]);

  const updateImageGen = (updater: (prev: ImageGenRow[]) => ImageGenRow[]): void => {
    setServerViolations([]);
    setImageGen((prev) => {
      const next = updater(imageGenRef.current.length > 0 ? imageGenRef.current : prev);
      imageGenRef.current = next;
      return next;
    });
  };
  const updateVideoGen = (updater: (prev: VideoGenRow[]) => VideoGenRow[]): void => {
    setServerViolations([]);
    setVideoGen((prev) => {
      const next = updater(videoGenRef.current.length > 0 ? videoGenRef.current : prev);
      videoGenRef.current = next;
      return next;
    });
  };

  const imageGenViolations = useMemo(
    () => validateMediaGenChains(imageGen),
    [imageGen],
  );
  const videoGenViolations = useMemo(
    () => validateMediaGenChains(videoGen, "videoGenModels.group"),
    [videoGen],
  );
  const violationsByRow = useMemo(() => {
    const m = new Map<number, Violation[]>();
    for (const v of [...imageGenViolations, ...serverViolations.filter((item) => !item.field.startsWith("videoGenModels"))]) {
      const bucket = m.get(v.rowIndex);
      if (bucket) bucket.push(v);
      else m.set(v.rowIndex, [v]);
    }
    return m;
  }, [imageGenViolations, serverViolations]);
  const videoViolationsByRow = useMemo(() => {
    const map = new Map<number, Violation[]>();
    for (const violation of [
      ...videoGenViolations,
      ...serverViolations.filter((item) => item.field.startsWith("videoGenModels")),
    ]) {
      const bucket = map.get(violation.rowIndex) ?? [];
      bucket.push(violation);
      map.set(violation.rowIndex, bucket);
    }
    return map;
  }, [serverViolations, videoGenViolations]);
  const hasBlockingViolations = imageGenViolations.length > 0 || videoGenViolations.length > 0;

  const capabilityByBase = useMemo(() => {
    const map = new Map<string, ModelCatalogEntry>();
    for (const e of catalogEntries) {
      const base = baseId(e.modelId);
      if (!base) continue;
      const existing = map.get(base);
      if (!existing || (!existing.capabilities?.thinkingParam && !existing.capabilities?.outputTokenParam)) {
        map.set(base, e);
      }
    }
    return map;
  }, [catalogEntries]);

  const setModelParam = (model: string, patch: Partial<PlatformModelDefaultParams>): void => {
    setModelParams((current) => {
      const next = { ...current, [model]: { ...(current[model] ?? {}), ...patch } };
      const row = next[model];
      if (!row.reasoningEffort && row.maxTokens == null) delete next[model];
      return next;
    });
  };

  const catalogIndex = useMemo(() => buildCatalogIndex(catalogEntries), [catalogEntries]);
  const canonicalGrokMedia = useMemo(
    () => resolveCanonicalGrokMediaCatalog(catalogEntries),
    [catalogEntries],
  );
  const mediaCandidateById = useMemo(
    () => new Map(canonicalGrokMedia.map((entry) => [entry.modelId, entry])),
    [canonicalGrokMedia],
  );

  const visionCandidateRefs = useMemo(
    () => bucketCandidatesFromIndex(catalogIndex, "vision"),
    [catalogIndex],
  );
  const documentCandidateRefs = useMemo(
    () => bucketCandidatesFromIndex(catalogIndex, "document"),
    [catalogIndex],
  );
  const videoUnderstandCandidateRefs = useMemo(
    () => bucketCandidatesFromIndex(catalogIndex, "video-understand"),
    [catalogIndex],
  );
  const imageGenCandidateRefs = useMemo(
    () => imageGenCandidatesFromIndex(catalogIndex),
    [catalogIndex],
  );
  const videoGenCandidateRefs = useMemo(
    () => videoGenCandidatesFromIndex(catalogIndex),
    [catalogIndex],
  );
  const workCandidateRefs = useMemo(
    () => workModelCandidatesFromIndex(catalogIndex),
    [catalogIndex],
  );
  const videoFlagsById = useMemo(() => {
    const map = new Map<string, VideoGenFlags>();
    for (const item of videoGenCandidateRefs) {
      map.set(item.modelId, videoGenFlagsFromIndex(catalogIndex, item.modelId));
    }
    return map;
  }, [catalogIndex, videoGenCandidateRefs]);

  const visionCandidates = useMemo(
    () => visionCandidateRefs.map((item) => item.modelId),
    [visionCandidateRefs],
  );
  const imageGenCandidateIds = useMemo(
    () => imageGenCandidateRefs.map((item) => item.modelId),
    [imageGenCandidateRefs],
  );
  const videoGenCandidateIds = useMemo(
    () => videoGenCandidateRefs.map((item) => item.modelId),
    [videoGenCandidateRefs],
  );
  const workCandidateIds = useMemo(
    () => workCandidateRefs.map((item) => item.modelId),
    [workCandidateRefs],
  );

  const visionStoredIds = useStableStringList(vision.map((row) => row.modelId));
  const documentStoredIds = useStableStringList(documents.map((row) => row.modelId));
  const videoUnderstandStoredIds = useStableStringList(videoUnderstand.map((row) => row.modelId));
  const workStoredIds = useStableStringList(modelCallModels);
  const imageStoredIds = useStableStringList((() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const row of imageGen) {
      const id = row.modelId.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  })());
  const videoStoredIds = useStableStringList((() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const row of videoGen) {
      const id = row.modelId.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  })());

  const staleCuratedModels = useMemo(() => {
    if (catalogSource !== "live") return [];
    const draft: CurationData = {
      version: 1,
      editedAt: "",
      editedBy: "",
      visionModels: visionStoredIds.map((modelId) => ({ modelId, scenario: "" })),
      documentModels: documentStoredIds.map((modelId) => ({ modelId, scenario: "" })),
      videoModels: videoUnderstandStoredIds.map((modelId) => ({ modelId, scenario: "" })),
      imageGenModels: imageStoredIds.map((modelId) => ({ modelId, group: "", scenario: "" })),
      videoGenModels: videoStoredIds.map((modelId) => ({ modelId, group: "", scenario: "" })),
      modelCallModels: [...workStoredIds],
    };
    return reconcileCurationAgainstCatalog(draft, catalogEntries).staleCurated;
  }, [catalogSource, catalogEntries, visionStoredIds, documentStoredIds, videoUnderstandStoredIds, imageStoredIds, videoStoredIds, workStoredIds]);

  const imageGenParamSchemaByModel = useMemo(() => {
    const map = new Map<string, Record<string, string[]>>();
    for (const e of catalogEntries) {
      const schema = e.capabilities?.paramSchema;
      if (!schema || Object.keys(schema).length === 0) continue;
      const base = baseId(e.modelId);
      if (base && !map.has(base)) map.set(base, schema);
      if (e.modelId && !map.has(e.modelId)) map.set(e.modelId, schema);
    }
    return map;
  }, [catalogEntries]);
  const paramSchemaFor = (modelId: string): Record<string, string[]> | undefined =>
    imageGenParamSchemaByModel.get(modelId.trim())
    ?? imageGenParamSchemaByModel.get(baseId(modelId.trim()))
    ?? getGrokMediaModelContract(modelId)?.paramSchema;

  const visionListed = useMemo(
    () => listBucketModels({
      storedIds: visionStoredIds,
      candidates: visionCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: visionTouched,
      bucket: "vision",
    }),
    [visionStoredIds, visionCandidateRefs, catalogIndex, catalogSource, visionTouched],
  );
  const documentListed = useMemo(
    () => listBucketModels({
      storedIds: documentStoredIds,
      candidates: documentCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: documentTouched,
      bucket: "document",
    }),
    [documentStoredIds, documentCandidateRefs, catalogIndex, catalogSource, documentTouched],
  );
  const videoUnderstandListed = useMemo(
    () => listBucketModels({
      storedIds: videoUnderstandStoredIds,
      candidates: videoUnderstandCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: videoUnderstandTouched,
      bucket: "video-understand",
    }),
    [videoUnderstandStoredIds, videoUnderstandCandidateRefs, catalogIndex, catalogSource, videoUnderstandTouched],
  );
  const workListed = useMemo(
    () => listBucketModels({
      storedIds: workStoredIds,
      candidates: workCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: workTouched,
      bucket: "work",
    }),
    [workStoredIds, workCandidateRefs, catalogIndex, catalogSource, workTouched],
  );
  const imageListed = useMemo(
    () => listBucketModels({
      storedIds: imageStoredIds,
      candidates: imageGenCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: imageGenTouched,
      bucket: "image-gen",
    }),
    [imageStoredIds, imageGenCandidateRefs, catalogIndex, catalogSource, imageGenTouched],
  );
  const videoListed = useMemo(
    () => listBucketModels({
      storedIds: videoStoredIds,
      candidates: videoGenCandidateRefs,
      index: catalogIndex,
      catalogSource,
      touched: videoGenTouched,
      bucket: "video-gen",
    }),
    [videoStoredIds, videoGenCandidateRefs, catalogIndex, catalogSource, videoGenTouched],
  );
  const webSearchListed = useMemo(
    () => listWebSearchProviders({ stored: webSearchProviders, touched: webSearchTouched }),
    [webSearchProviders, webSearchTouched],
  );
  const audioTranscribeListed = useMemo(
    () => listAudioTranscribeProviders({
      stored: audioTranscribeProviders,
      touched: audioTranscribeTouched,
    }),
    [audioTranscribeProviders, audioTranscribeTouched],
  );

  const materializeVision = (prev: VisionRow[], candidates: typeof visionCandidateRefs): VisionRow[] => {
    if (prev.length > 0) return prev;
    return candidates.map((item) => ({ modelId: item.modelId, scenario: "" }));
  };

  const setChainModelId = (indices: number[], value: string): void => {
    const candidate = mediaCandidateById.get(value.trim());
    updateImageGen((prev) => prev.map((r, k) => {
      if (!indices.includes(k)) return r;
      const paramSchema = candidate?.capabilities?.paramSchema ?? paramSchemaFor(value);
      return {
        ...r,
        modelId: value,
        group: r.group || candidate?.chosenGroup.name || "",
        paramSchema,
        paramDefaults: narrowParamDefaults(r.paramDefaults, paramSchema),
      };
    }));
  };
  const setChainRowField = (
    i: number,
    field: "group" | "scenario" | "unsupportedParams",
    value: string,
  ): void => {
    updateImageGen((prev) => prev.map((r, k) => (k === i ? { ...r, [field]: value } : r)));
  };
  const swapImageGenRows = (a: number, b: number): void => {
    updateImageGen((prev) => {
      const next = [...prev];
      const left = next[a];
      const right = next[b];
      if (left === undefined || right === undefined) return prev;
      next[a] = right;
      next[b] = left;
      return next;
    });
  };
  const addChainRow = (group: ImageGenChainGroup): void => {
    const sample = group.rows[0]?.row;
    if (!sample) return;
    const lastIdx = Math.max(...group.rows.map((r) => r.index));
    const newRow: ImageGenRow = {
      uid: nextRowUid(),
      modelId: sample.modelId,
      group: "",
      scenario: "",
      unsupportedParams: "",
    };
    updateImageGen((prev) => {
      const next = [...prev];
      next.splice(lastIdx + 1, 0, newRow);
      return next;
    });
  };
  const removeImageGenRows = (indices: Set<number>): void => {
    updateImageGen((prev) => prev.filter((_, k) => !indices.has(k)));
  };

  const setChainParamDefault = (indices: number[], param: string, value: string): void => {
    updateImageGen((prev) =>
      prev.map((r, k) => {
        if (!indices.includes(k)) return r;
        const next = { ...(r.paramDefaults ?? {}) };
        if (value) next[param] = value;
        else delete next[param];
        return { ...r, paramDefaults: Object.keys(next).length > 0 ? next : undefined };
      }),
    );
  };
  const isParamOffForRow = (row: ImageGenRow, param: string): boolean =>
    row.unsupportedParams.split(",").map((s) => s.trim()).includes(param);
  const setParamOffForRow = (i: number, param: string, off: boolean): void => {
    updateImageGen((prev) =>
      prev.map((r, k) => {
        if (k !== i) return r;
        const items = r.unsupportedParams.split(",").map((s) => s.trim()).filter(Boolean);
        const has = items.includes(param);
        let nextItems: string[];
        if (off && !has) nextItems = [...items, param];
        else if (!off && has) nextItems = items.filter((x) => x !== param);
        else nextItems = items;
        return { ...r, unsupportedParams: nextItems.join(", ") };
      }),
    );
  };

  const takeImageGen = (prev: ImageGenRow[]): ImageGenRow[] => {
    if (prev.length > 0 || imageGenTouched) return prev;
    return imageGenCandidateRefs.map((item) => toImageGenRow({
      modelId: item.modelId,
      group: defaultGroupIndexed(catalogIndex, item.modelId),
      scenario: "",
    }));
  };
  const takeVideoGen = (prev: VideoGenRow[]): VideoGenRow[] => {
    if (prev.length > 0 || videoGenTouched) return prev;
    return videoGenCandidateRefs.map((item) => toVideoGenRow({
      modelId: item.modelId,
      group: defaultGroupIndexed(catalogIndex, item.modelId),
      scenario: "",
    }));
  };

  const visionForSave = (): VisionRow[] => {
    if (visionRef.current.length > 0 || visionTouched) return visionRef.current;
    return visionCandidateRefs.map((item) => ({ modelId: item.modelId, scenario: "" }));
  };
  const documentsForSave = (): VisionRow[] => {
    if (documentsRef.current.length > 0 || documentTouched) return documentsRef.current;
    return documentCandidateRefs.map((item) => ({ modelId: item.modelId, scenario: "" }));
  };
  const videoUnderstandForSave = (): VisionRow[] => {
    if (videoUnderstandRef.current.length > 0 || videoUnderstandTouched) return videoUnderstandRef.current;
    return videoUnderstandCandidateRefs.map((item) => ({ modelId: item.modelId, scenario: "" }));
  };
  const workForSave = (): string[] => {
    if (modelCallModels.length > 0 || workTouched) return modelCallModels;
    return workCandidateRefs.map((item) => item.modelId);
  };
  const webSearchForSave = (): WebSearchProviderRow[] => {
    if (webSearchRef.current.length > 0 || webSearchTouched) return webSearchRef.current;
    return webSearchListed.filter((row) => row.enabled).map((row) => ({
      provider: row.provider,
      group: row.group,
    }));
  };
  const audioTranscribeForSave = (): AudioTranscribeProviderRow[] => {
    if (audioTranscribeRef.current.length > 0 || audioTranscribeTouched) return audioTranscribeRef.current;
    return audioTranscribeListed.filter((row) => row.enabled).map((row) => ({
      provider: row.provider,
      group: row.group,
    }));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    setServerViolations([]);
    const imageGenBody = takeImageGen(imageGenRef.current.length > 0 ? imageGenRef.current : imageGen);
    const videoGenBody = takeVideoGen(videoGenRef.current.length > 0 ? videoGenRef.current : videoGen);
    try {
      const body = {
        visionModels: visionForSave().map((v) => ({ modelId: v.modelId.trim(), scenario: v.scenario.trim() })),
        documentModels: documentsForSave().map((v) => ({ modelId: v.modelId.trim(), scenario: v.scenario.trim() })),
        videoModels: videoUnderstandForSave().map((v) => ({ modelId: v.modelId.trim(), scenario: v.scenario.trim() })),
        imageGenModels: imageGenBody.map((v) => {
          const row: {
            modelId: string;
            group: string;
            scenario: string;
            unsupportedParams?: string[];
            paramSchema?: Record<string, string[]>;
            paramDefaults?: Record<string, string>;
          } = {
            modelId: v.modelId.trim(),
            group: v.group.trim(),
            scenario: v.scenario.trim(),
          };
          const unsupportedParams = v.unsupportedParams.split(",").map((s) => s.trim()).filter(Boolean);
          if (unsupportedParams.length > 0) row.unsupportedParams = unsupportedParams;
          const effectiveSchema = v.paramSchema ?? paramSchemaFor(v.modelId);
          if (effectiveSchema) row.paramSchema = effectiveSchema;
          if (v.paramDefaults && Object.keys(v.paramDefaults).length > 0) row.paramDefaults = v.paramDefaults;
          return row;
        }),
        videoGenModels: videoGenBody.map((v) => ({
          modelId: v.modelId.trim(),
          group: v.group.trim(),
          scenario: v.scenario.trim(),
          ...(v.unsupportedParams?.length ? { unsupportedParams: v.unsupportedParams } : {}),
          ...(v.paramSchema ? { paramSchema: v.paramSchema } : {}),
          ...(v.paramDefaults && Object.keys(v.paramDefaults).length > 0 ? { paramDefaults: v.paramDefaults } : {}),
        })),
        imageFallbackChain: fallbackRef.current.split(",").map((s) => s.trim()).filter(Boolean),
        modelCallModels: workForSave().map((s) => s.trim()).filter(Boolean),
        modelParams,
        // 两条链一律带 group（REQ-268）：缺 group 的行由服务端唯一构建器丢弃。
        webSearchProviders: webSearchForSave().map((p) => ({
          provider: p.provider,
          group: p.group.trim(),
        })),
        audioTranscribeProviders: audioTranscribeForSave().map((p) => ({
          provider: p.provider,
          group: p.group.trim(),
        })),
        ...(xSearchEnabled && xSearchGroupRef.current.trim()
          ? { xSearch: { enabled: true, group: xSearchGroupRef.current.trim() } }
          : {}),
      };
      const r = await apiCall<{ curation: CurationData }>(
        "PUT",
        platformToolCurationPath(),
        { token, body },
      );
      const c = r.curation;
      const savedVision = c.visionModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
      visionRef.current = savedVision;
      setVision(savedVision);
      const savedDocs = c.documentModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
      documentsRef.current = savedDocs;
      setDocuments(savedDocs);
      const savedVu = c.videoModels?.map((v) => ({ modelId: v.modelId, scenario: v.scenario })) ?? [];
      videoUnderstandRef.current = savedVu;
      setVideoUnderstand(savedVu);
      const savedImage = c.imageGenModels?.map(toImageGenRow) ?? [];
      imageGenRef.current = savedImage;
      setImageGen(savedImage);
      const savedVideo = c.videoGenModels?.map(toVideoGenRow) ?? [];
      videoGenRef.current = savedVideo;
      setVideoGen(savedVideo);
      setModelParams(c.modelParams ?? {});
      const savedFallback = (c.imageFallbackChain ?? []).join(", ");
      fallbackRef.current = savedFallback;
      setFallbackChain(savedFallback);
      setModelCallModels(c.modelCallModels ?? []);
      const savedWeb = c.webSearchProviders?.length
        ? c.webSearchProviders.map((p) => ({ provider: p.provider, group: p.group ?? "" }))
        : [];
      webSearchRef.current = savedWeb;
      setWebSearchProviders(savedWeb);
      const savedAudio = c.audioTranscribeProviders?.length
        ? c.audioTranscribeProviders.map((p) => ({ provider: p.provider, group: p.group ?? "" }))
        : [];
      audioTranscribeRef.current = savedAudio;
      setAudioTranscribeProviders(savedAudio);
      setXSearchEnabled(c.xSearch?.enabled === true);
      const savedX = c.xSearch?.group ?? "";
      xSearchGroupRef.current = savedX;
      setXSearchGroup(savedX);
      setVisionTouched(false);
      setDocumentTouched(false);
      setVideoUnderstandTouched(false);
      setImageGenTouched(false);
      setVideoGenTouched(false);
      setWorkTouched(false);
      setWebSearchTouched(false);
      setAudioTranscribeTouched(false);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.details)) {
        setServerViolations(err.details as Violation[]);
      }
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const configuredCount =
    (vision.length ? 1 : 0) + (documents.length ? 1 : 0) + (videoUnderstand.length ? 1 : 0)
    + (imageGen.length ? 1 : 0) + (videoGen.length ? 1 : 0) + (fallbackChain.trim() ? 1 : 0)
    + (modelCallModels.some((m) => m.trim()) ? 1 : 0);

  const WEB_SEARCH_GROUP_PLACEHOLDER: Record<WebSearchProviderId, string> = {
    tavily: t("admin.toolCuration.webSearch.group.tavily"),
    deepseek_web: t("admin.toolCuration.webSearch.group.deepseek_web"),
    glm_web: t("admin.toolCuration.webSearch.group.glm_web"),
    grok_web: t("admin.toolCuration.webSearch.group.grok_web"),
    openai_web: t("admin.toolCuration.webSearch.group.openai_web"),
  };
  const WEB_SEARCH_PROVIDER_OPTION_LABELS: Record<WebSearchProviderId, string> = {
    deepseek_web: t("admin.toolCuration.webSearch.provider.deepseek_web"),
    glm_web: t("admin.toolCuration.webSearch.provider.glm_web"),
    grok_web: t("admin.toolCuration.webSearch.provider.grok_web"),
    openai_web: t("admin.toolCuration.webSearch.provider.openai_web"),
    tavily: t("admin.toolCuration.webSearch.provider.tavily"),
  };
  const AUDIO_TRANSCRIBE_GROUP_PLACEHOLDER: Record<AudioTranscribeProviderId, string> = {
    volc_asr: t("admin.toolCuration.audioTranscribe.group.volc_asr"),
    gemini_model: t("admin.toolCuration.audioTranscribe.group.gemini_model"),
  };
  const AUDIO_TRANSCRIBE_PROVIDER_OPTION_LABELS: Record<AudioTranscribeProviderId, string> = {
    volc_asr: t("admin.toolCuration.audioTranscribe.provider.volc_asr"),
    gemini_model: t("admin.toolCuration.audioTranscribe.provider.gemini_model"),
  };
  const VIDEO_MODE_LABELS: Record<string, string> = {
    text_to_video: t("admin.toolCuration.videoMode.text_to_video"),
    image_to_video: t("admin.toolCuration.videoMode.image_to_video"),
    reference_to_video: t("admin.toolCuration.videoMode.reference_to_video"),
    edit: t("admin.toolCuration.videoMode.edit"),
    extension: t("admin.toolCuration.videoMode.extension"),
  };

  const ineligibleText = (rejection: ToolAdmissionRejection | undefined, work: boolean): string => {
    if (rejection === "generation-endpoint" && work) {
      return t("admin.toolCuration.reason.generation-endpoint-work");
    }
    if (rejection === "generation-endpoint") return t("admin.toolCuration.reason.generation-endpoint");
    if (rejection === "capability-missing") return t("admin.toolCuration.reason.capability-missing");
    if (rejection === "no-speakable-leg") return t("admin.toolCuration.reason.no-speakable-leg");
    if (rejection === "not-in-catalog") return t("admin.toolCuration.reason.not-in-catalog");
    return t("admin.toolCuration.reason.capability-missing");
  };

  const toggleVisionRow = useCallback((id: string, enabled: boolean): void => {
    setVisionTouched(true);
    setVision((prev) => {
      const base = materializeVision(visionRef.current.length > 0 ? visionRef.current : prev, visionCandidateRefs);
      const ids = toggleListed(base.map((row) => row.modelId), id, enabled);
      const byId = new Map(base.map((row) => [row.modelId, row]));
      const next = ids.map((modelId) => byId.get(modelId) ?? { modelId, scenario: "" });
      visionRef.current = next;
      return next;
    });
  }, [visionCandidateRefs]);
  const onVisionScenario = useCallback((id: string, scenario: string): void => {
    setVisionTouched(true);
    const prev = visionRef.current;
    const base = materializeVision(prev, visionCandidateRefs);
    const next = base.map((row) => (row.modelId === id ? { ...row, scenario } : row));
    const wasEmpty = prev.length === 0;
    visionRef.current = next;
    if (wasEmpty) setVision(next);
  }, [visionCandidateRefs]);
  const toggleDocumentRow = useCallback((id: string, enabled: boolean): void => {
    setDocumentTouched(true);
    setDocuments((prev) => {
      const base = materializeVision(documentsRef.current.length > 0 ? documentsRef.current : prev, documentCandidateRefs);
      const ids = toggleListed(base.map((row) => row.modelId), id, enabled);
      const byId = new Map(base.map((row) => [row.modelId, row]));
      const next = ids.map((modelId) => byId.get(modelId) ?? { modelId, scenario: "" });
      documentsRef.current = next;
      return next;
    });
  }, [documentCandidateRefs]);
  const onDocumentScenario = useCallback((id: string, scenario: string): void => {
    setDocumentTouched(true);
    const prev = documentsRef.current;
    const base = materializeVision(prev, documentCandidateRefs);
    const next = base.map((row) => (row.modelId === id ? { ...row, scenario } : row));
    const wasEmpty = prev.length === 0;
    documentsRef.current = next;
    if (wasEmpty) setDocuments(next);
  }, [documentCandidateRefs]);
  const toggleVideoUnderstandRow = useCallback((id: string, enabled: boolean): void => {
    setVideoUnderstandTouched(true);
    setVideoUnderstand((prev) => {
      const base = materializeVision(
        videoUnderstandRef.current.length > 0 ? videoUnderstandRef.current : prev,
        videoUnderstandCandidateRefs,
      );
      const ids = toggleListed(base.map((row) => row.modelId), id, enabled);
      const byId = new Map(base.map((row) => [row.modelId, row]));
      const next = ids.map((modelId) => byId.get(modelId) ?? { modelId, scenario: "" });
      videoUnderstandRef.current = next;
      return next;
    });
  }, [videoUnderstandCandidateRefs]);
  const onVideoUnderstandScenario = useCallback((id: string, scenario: string): void => {
    setVideoUnderstandTouched(true);
    const prev = videoUnderstandRef.current;
    const base = materializeVision(prev, videoUnderstandCandidateRefs);
    const next = base.map((row) => (row.modelId === id ? { ...row, scenario } : row));
    const wasEmpty = prev.length === 0;
    videoUnderstandRef.current = next;
    if (wasEmpty) setVideoUnderstand(next);
  }, [videoUnderstandCandidateRefs]);
  const toggleWorkRow = useCallback((id: string, enabled: boolean): void => {
    setWorkTouched(true);
    setModelCallModels((prev) => {
      const base = prev.length > 0 ? prev : workCandidateRefs.map((item) => item.modelId);
      return toggleListed(base, id, enabled);
    });
  }, [workCandidateRefs]);

  const moveVisionEnabled = useCallback((enabledIndex: number, dir: -1 | 1): void => {
    setVisionTouched(true);
    setVision((prev) => {
      const base = materializeVision(visionRef.current.length > 0 ? visionRef.current : prev, visionCandidateRefs);
      const next = moveIndex(base, enabledIndex, dir);
      visionRef.current = next;
      return next;
    });
  }, [visionCandidateRefs]);
  const moveDocumentEnabled = useCallback((enabledIndex: number, dir: -1 | 1): void => {
    setDocumentTouched(true);
    setDocuments((prev) => {
      const next = moveIndex(
        materializeVision(documentsRef.current.length > 0 ? documentsRef.current : prev, documentCandidateRefs),
        enabledIndex,
        dir,
      );
      documentsRef.current = next;
      return next;
    });
  }, [documentCandidateRefs]);
  const moveVideoUnderstandEnabled = useCallback((enabledIndex: number, dir: -1 | 1): void => {
    setVideoUnderstandTouched(true);
    setVideoUnderstand((prev) => {
      const next = moveIndex(
        materializeVision(
          videoUnderstandRef.current.length > 0 ? videoUnderstandRef.current : prev,
          videoUnderstandCandidateRefs,
        ),
        enabledIndex,
        dir,
      );
      videoUnderstandRef.current = next;
      return next;
    });
  }, [videoUnderstandCandidateRefs]);

  const toggleImageModel = (id: string, enabled: boolean): void => {
    setImageGenTouched(true);
    updateImageGen((prev) => {
      const base = takeImageGen(prev);
      if (!enabled) return base.filter((row) => row.modelId !== id);
      if (base.some((row) => row.modelId === id)) return base;
      return [
        ...base,
        toImageGenRow({
          modelId: id,
          group: defaultGroupIndexed(catalogIndex, id),
          scenario: "",
        }),
      ];
    });
  };

  const toggleVideoModel = (id: string, enabled: boolean): void => {
    setVideoGenTouched(true);
    updateVideoGen((prev) => {
      const base = takeVideoGen(prev);
      if (!enabled) return base.filter((row) => row.modelId !== id);
      if (base.some((row) => row.modelId === id)) return base;
      const candidate = mediaCandidateById.get(id.trim());
      return [
        ...base,
        toVideoGenRow({
          modelId: id,
          group: defaultGroupIndexed(catalogIndex, id) || candidate?.chosenGroup.name || "",
          scenario: id.includes("1.5") ? "高质量视频" : "快速视频",
          paramSchema: candidate?.capabilities?.paramSchema ?? getGrokMediaModelContract(id)?.paramSchema,
        }),
      ];
    });
  };

  const moveImageModel = (modelId: string, dir: -1 | 1): void => {
    setImageGenTouched(true);
    updateImageGen((prev) => {
      const base = takeImageGen(prev);
      const groups = groupImageGenChains(base);
      const idx = groups.findIndex((g) => g.key === modelId);
      if (idx < 0) return base;
      const nextGroups = moveIndex(groups, idx, dir);
      return nextGroups.flatMap((g) => g.rows.map((item) => item.row));
    });
  };

  const moveVideoModel = (index: number, dir: -1 | 1): void => {
    setVideoGenTouched(true);
    updateVideoGen((prev) => moveIndex(takeVideoGen(prev), index, dir));
  };

  const visionScenarioById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of vision) map[row.modelId] = row.scenario;
    return map;
  }, [vision]);
  const documentScenarioById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of documents) map[row.modelId] = row.scenario;
    return map;
  }, [documents]);
  const videoUnderstandScenarioById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of videoUnderstand) map[row.modelId] = row.scenario;
    return map;
  }, [videoUnderstand]);

  const imageDisplayGroups = useMemo(() => {
    if (imageGen.length > 0 || imageGenTouched) return groupImageGenChains(imageGen);
    return imageGenCandidateRefs.map((item, index) => ({
      key: item.modelId,
      displayName: item.modelId,
      rows: [{
        row: {
          uid: `syn_${item.modelId}`,
          modelId: item.modelId,
          group: defaultGroupIndexed(catalogIndex, item.modelId),
          scenario: "",
          unsupportedParams: "",
          paramSchema: paramSchemaFor(item.modelId),
          paramDefaults: undefined,
        },
        index,
      }],
    }));
  }, [imageGen, imageGenTouched, imageGenCandidateRefs, catalogIndex, imageGenParamSchemaByModel]);
  const imageGroupsByKey = useMemo(() => {
    const map = new Map<string, ImageGenChainGroup>();
    for (const group of imageDisplayGroups) map.set(group.key, group);
    return map;
  }, [imageDisplayGroups]);

  const videoDisplayRows = useMemo(() => {
    if (videoGen.length > 0 || videoGenTouched) return videoGen;
    return videoGenCandidateRefs.map((item): VideoGenRow => ({
      uid: `syn_${item.modelId}`,
      modelId: item.modelId,
      group: defaultGroupIndexed(catalogIndex, item.modelId),
      scenario: "",
      paramSchema: getGrokMediaModelContract(item.modelId)?.paramSchema,
      paramDefaults: undefined,
    }));
  }, [videoGen, videoGenTouched, videoGenCandidateRefs, catalogIndex]);

  const enabledVisionCount = visionListed.filter((item) => item.enabled).length;
  const enabledDocumentCount = documentListed.filter((item) => item.enabled).length;
  const enabledVideoUnderstandCount = videoUnderstandListed.filter((item) => item.enabled).length;
  const enabledImageCount = imageListed.filter((item) => item.enabled).length;
  const enabledVideoCount = videoListed.filter((item) => item.enabled).length;
  const enabledWorkCount = workListed.filter((item) => item.enabled).length;

  return (
    <div className="max-w-4xl space-y-5">
      <datalist id="tool-vision-candidates">
        {visionCandidates.map((id) => <option key={id} value={id} />)}
      </datalist>
      <datalist id="tool-imagegen-candidates">
        {imageGenCandidateIds.map((id) => <option key={id} value={id} />)}
      </datalist>
      <datalist id="tool-videogen-candidates">
        {videoGenCandidateIds.map((id) => <option key={id} value={id} />)}
      </datalist>
      <datalist id="tool-workmodel-candidates">
        {workCandidateIds.map((id) => <option key={id} value={id} />)}
      </datalist>

      <div>
        <h1 className="text-lg font-semibold">{t("admin.toolCuration.title")}</h1>
        <p className="mt-1 text-sm text-dosia-text-secondary">
          {t("admin.toolCuration.subtitle")}
        </p>
        {catalogSource === "live" ? (
          <p className="mt-1 text-xs text-dosia-text-secondary">{t("admin.toolCuration.catalogLive")}</p>
        ) : !catalogLoading && (
          // 网关不可达：候选如实留空（不回落随包快照，N-95），hook 每 30s 后台重拉，恢复后自动出现。
          <div className="mt-2 rounded border border-dosia-warning/40 bg-dosia-warning/15 px-3 py-2 text-xs font-medium text-dosia-warning">
            {t("admin.modelCatalog.unavailable")}
          </div>
        )}
      </div>

      {staleCuratedModels.length > 0 && (
        <div
          data-testid="stale-curation-banner"
          className="rounded border border-dosia-danger/40 bg-dosia-danger/15 px-3 py-2 text-xs text-dosia-danger"
        >
          <b>{t("admin.toolCuration.staleBanner.title", { count: staleCuratedModels.length })}</b>
          {t("admin.toolCuration.staleBanner.body")}
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {staleCuratedModels.map((s) => (
              <li key={`${s.section}:${s.modelId}`}>
                <span className="text-dosia-text-secondary">{s.section}</span> · <code>{s.modelId}</code>
              </li>
            ))}
          </ul>
          <p className="mt-1">{t("admin.toolCuration.staleBanner.hint")}</p>
        </div>
      )}

      <div className="rounded border border-dosia-warning/30 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning">
        {t("admin.toolCuration.emptyMeansUnavailable")}
      </div>

      {curationQ.error && <ErrorBanner>{curationQ.error.message}</ErrorBanner>}
      {saveError && (
        <div className="rounded border border-dosia-danger/30 bg-dosia-danger/10 px-3 py-2 text-sm text-dosia-danger">
          {t("admin.toolCuration.saveFailed", { message: saveError })}
        </div>
      )}

      {!curationLoaded ? (
        <p className="rounded border border-dashed border-dosia-border px-3 py-6 text-center text-sm text-dosia-text-secondary">
          {t("admin.toolCuration.loading")}
        </p>
      ) : (
      <>
      <ToolCard
        title={t("admin.toolCuration.card.vision.title")}
        tool={t("admin.toolCuration.card.vision.tool")}
        blurb={t("admin.toolCuration.card.vision.blurb")}
        enabled={enabledVisionCount}
        total={visionCandidateRefs.length}
      >
        <ModelPickList
          items={visionListed}
          ariaPrefix="vision-modelId"
          sortable
          scenarioPlaceholder={t("admin.toolCuration.scenario.placeholder")}
          scenarioById={visionScenarioById}
          onScenario={onVisionScenario}
          onToggle={toggleVisionRow}
          onMove={moveVisionEnabled}
          ineligibleKind="other"
        />
        <section className="space-y-1 pt-2">
          <h3 className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.fallback.title")}</h3>
          <IsolatedDraftInput
            key={fallbackChain}
            initial={fallbackChain}
            placeholder={t("admin.toolCuration.fallback.placeholder")}
            onDraft={(next) => {
              fallbackRef.current = next;
            }}
          />
          <p className="text-xs text-dosia-text-dim">{t("admin.toolCuration.fallback.hint")}</p>
          <ModelSelect
            value=""
            candidates={enabledIdsOf(visionListed).filter((id) => !fallbackChain.split(",").map((s) => s.trim()).includes(id))}
            placeholder={t("admin.toolCuration.fallback.add")}
            ariaLabel="fallback-add"
            onChange={(next) => {
              if (!next) return;
              const ids = fallbackRef.current.split(",").map((s) => s.trim()).filter(Boolean);
              if (ids.includes(next)) return;
              const joined = [...ids, next].join(", ");
              fallbackRef.current = joined;
              setFallbackChain(joined);
            }}
            className="max-w-md"
          />
        </section>
      </ToolCard>

      <ToolCard
        title={t("admin.toolCuration.card.document.title")}
        tool={t("admin.toolCuration.card.document.tool")}
        blurb={t("admin.toolCuration.card.document.blurb")}
        enabled={enabledDocumentCount}
        total={documentCandidateRefs.length}
      >
        <ModelPickList
          items={documentListed}
          ariaPrefix="document-modelId"
          sortable
          scenarioPlaceholder={t("admin.toolCuration.scenario.placeholder")}
          scenarioById={documentScenarioById}
          onScenario={onDocumentScenario}
          onToggle={toggleDocumentRow}
          onMove={moveDocumentEnabled}
          ineligibleKind="other"
        />
      </ToolCard>

      <ToolCard
        title={t("admin.toolCuration.card.videoUnderstand.title")}
        tool={t("admin.toolCuration.card.videoUnderstand.tool")}
        blurb={t("admin.toolCuration.card.videoUnderstand.blurb")}
        enabled={enabledVideoUnderstandCount}
        total={videoUnderstandCandidateRefs.length}
      >
        <ModelPickList
          items={videoUnderstandListed}
          ariaPrefix="video-modelId"
          sortable
          scenarioPlaceholder={t("admin.toolCuration.scenario.placeholder")}
          scenarioById={videoUnderstandScenarioById}
          onScenario={onVideoUnderstandScenario}
          onToggle={toggleVideoUnderstandRow}
          onMove={moveVideoUnderstandEnabled}
          ineligibleKind="other"
        />
      </ToolCard>

      <ToolCard
        title={t("admin.toolCuration.card.imageGen.title")}
        tool={t("admin.toolCuration.card.imageGen.tool")}
        blurb={t("admin.toolCuration.card.imageGen.blurb")}
        enabled={enabledImageCount}
        total={imageGenCandidateRefs.length}
      >
        <p className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.enabledGroup")}</p>
        <div className="space-y-3">
          {imageListed.filter((item) => item.enabled).map((item) => {
            const group = imageGroupsByKey.get(item.modelId);
            if (!group) {
              return (
                <label key={item.modelId} className="flex items-center gap-2 text-sm" data-model-id={item.modelId}>
                  <input
                    type="checkbox"
                    checked
                    aria-label={`image-candidate-${item.modelId}`}
                    onChange={(e) => toggleImageModel(item.modelId, e.target.checked)}
                    className="rounded border-dosia-border"
                  />
                  <span>{formatProtocolLabel(item.modelId, item.protocol)}</span>
                  <WarningBadge item={item} ineligibleText={ineligibleText(item.rejection, false)} />
                </label>
              );
            }
            const groupIndices = group.rows.map((r) => r.index);
            const firstIndex = group.rows[0]?.index ?? 0;
            const cardUid = group.rows[0]?.row.uid ?? item.modelId;
            const cardModelId = group.rows[0]?.row.modelId ?? item.modelId;
            const schema = paramSchemaFor(cardModelId);
            const paramKeys = schema ? Object.keys(schema) : [];
            const cardParamDefaults = group.rows[0]?.row.paramDefaults ?? {};
            const paramPanelOpen = openImageParamCard === cardUid;
            const enabledIndex = imageListed.filter((x) => x.enabled).findIndex((x) => x.modelId === item.modelId);
            return (
              <div key={cardUid} className="space-y-2 rounded border border-dosia-border/60 p-3" data-model-id={item.modelId}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked
                    aria-label={`image-enabled-${item.modelId}`}
                    onChange={(e) => toggleImageModel(item.modelId, e.target.checked)}
                    className="rounded border-dosia-border"
                  />
                  <input
                    readOnly
                    aria-label={`modelId-${firstIndex}`}
                    value={cardModelId}
                    className="flex-[2] rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
                  />
                  <span className="text-xs text-dosia-text-secondary">
                    {item.protocol ? `· ${item.protocol}` : ""}
                  </span>
                  <WarningBadge item={item} ineligibleText={ineligibleText(item.rejection, false)} />
                  <button type="button" aria-label={`image-上移-${enabledIndex}`} onClick={() => moveImageModel(item.modelId, -1)} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↑</button>
                  <button type="button" aria-label={`image-下移-${enabledIndex}`} onClick={() => moveImageModel(item.modelId, 1)} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↓</button>
                  <button
                    type="button"
                    aria-label="删除模型"
                    onClick={() => {
                      setImageGenTouched(true);
                      toggleImageModel(item.modelId, false);
                    }}
                    className="px-1.5 py-0.5 rounded hover:bg-dosia-hover text-dosia-danger"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-dosia-text-dim">{t("admin.toolCuration.card.imageGen.channels")}</p>
                  {group.rows.map(({ row, index }, pos) => {
                    const rowViolations = violationsByRow.get(index) ?? [];
                    const flagged = rowViolations.length > 0;
                    return (
                      <div
                        key={row.uid}
                        className={`space-y-1 rounded border p-2 ${
                          flagged ? "border-dosia-danger/30 bg-dosia-danger/10" : "border-dosia-border/40"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dosia-border text-xs text-dosia-text-secondary"
                          >
                            {pos + 1}
                          </span>
                          <IsolatedDraftInput
                            initial={row.group}
                            placeholder={t("admin.toolCuration.group.placeholder")}
                            aria-label={`group-${index}`}
                            onDraft={(next) => {
                              setImageGenTouched(true);
                              const base = imageGenRef.current.length > 0
                                ? imageGenRef.current
                                : takeImageGen([]);
                              if (imageGenRef.current.length === 0) {
                                imageGenRef.current = base;
                                updateImageGen(() => base);
                              }
                              const mapped = (imageGenRef.current.length > 0 ? imageGenRef.current : base).map((r, k) =>
                                k === index ? { ...r, group: next } : r,
                              );
                              imageGenRef.current = mapped;
                            }}
                            className="flex-[2]"
                          />
                          <IsolatedDraftInput
                            initial={row.scenario}
                            placeholder={t("admin.toolCuration.channel.placeholder")}
                            aria-label={`scenario-${index}`}
                            onDraft={(next) => {
                              setImageGenTouched(true);
                              const base = imageGenRef.current.length > 0
                                ? imageGenRef.current
                                : takeImageGen([]);
                              if (imageGenRef.current.length === 0) {
                                imageGenRef.current = base;
                                updateImageGen(() => base);
                              }
                              const mapped = (imageGenRef.current.length > 0 ? imageGenRef.current : base).map((r, k) =>
                                k === index ? { ...r, scenario: next } : r,
                              );
                              imageGenRef.current = mapped;
                            }}
                            className="flex-[3]"
                          />
                          <RowControls
                            onUp={() => pos > 0 && swapImageGenRows(index, group.rows[pos - 1]!.index)}
                            onDown={() =>
                              pos < group.rows.length - 1 &&
                              swapImageGenRows(index, group.rows[pos + 1]!.index)
                            }
                            onRemove={() => {
                              setImageGenTouched(true);
                              removeImageGenRows(new Set([index]));
                            }}
                          />
                        </div>
                        {rowViolations.map((v, vi) => (
                          <p key={vi} role="alert" className="text-xs text-dosia-danger">
                            {v.message}
                          </p>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3">
                  <SecondaryButton className="text-xs" onClick={() => {
                    setImageGenTouched(true);
                    if (imageGen.length === 0) updateImageGen(() => takeImageGen([]));
                    addChainRow(group);
                  }}>
                    {t("admin.toolCuration.card.imageGen.addChannel")}
                  </SecondaryButton>
                  <button
                    type="button"
                    aria-label={`配置参数-${firstIndex}`}
                    onClick={() => setOpenImageParamCard((cur) => (cur === cardUid ? null : cardUid))}
                    className="text-xs font-medium text-dosia-accent hover:underline"
                  >
                    {paramPanelOpen ? t("admin.toolCuration.params.collapse") : t("admin.toolCuration.params.open")}
                  </button>
                </div>
                {paramPanelOpen &&
                  (paramKeys.length === 0 ? (
                    <p className="rounded border border-dashed border-dosia-border px-3 py-2 text-xs text-dosia-text-dim">
                      {t("admin.toolCuration.params.empty", { model: cardModelId || t("admin.toolCuration.params.noModel") })}
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded border border-dosia-border/60">
                      <table className="w-full text-xs" aria-label={`参数编辑器-${firstIndex}`}>
                        <thead>
                          <tr className="text-dosia-text-secondary">
                            <th className="px-2 py-1 text-left font-medium">{t("admin.toolCuration.params.param")}</th>
                            <th className="px-2 py-1 text-left font-medium">{t("admin.toolCuration.params.range")}</th>
                            <th className="px-2 py-1 text-left font-medium">{t("admin.toolCuration.params.default")}</th>
                            {group.rows.map(({ row, index }, pos) => (
                              <th key={row.uid} className="px-2 py-1 text-center font-medium">
                                {row.group.trim() || t("admin.toolCuration.params.channelN", { n: pos + 1 })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paramKeys.map((param) => {
                            const allowed = schema?.[param] ?? [];
                            return (
                              <tr key={param} className="border-t border-dosia-border/40">
                                <td className="px-2 py-1 font-medium text-dosia-text">{param}</td>
                                <td className="px-2 py-1 text-dosia-text-dim">{allowed.join(" / ")}</td>
                                <td className="px-2 py-1">
                                  <Select
                                    value={cardParamDefaults[param] ?? ""}
                                    aria-label={`默认值-${param}-${firstIndex}`}
                                    onChange={(e) => {
                                      setImageGenTouched(true);
                                      setChainParamDefault(groupIndices, param, e.target.value);
                                    }}
                                  >
                                    <option value="">{t("admin.toolCuration.params.followGateway")}</option>
                                    {allowed.map((v) => (
                                      <option key={v} value={v}>{v}</option>
                                    ))}
                                  </Select>
                                </td>
                                {group.rows.map(({ row, index }) => (
                                  <td key={row.uid} className="px-2 py-1 text-center">
                                    <input
                                      type="checkbox"
                                      aria-label={`通道开关-${param}-${index}`}
                                      checked={!isParamOffForRow(row, param)}
                                      onChange={(e) => {
                                        setImageGenTouched(true);
                                        setParamOffForRow(index, param, !e.target.checked);
                                      }}
                                      className="accent-dosia-accent"
                                    />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <p className="px-2 py-1 text-xs text-dosia-text-dim">
                        {t("admin.toolCuration.params.hint")}
                      </p>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
        {imageListed.some((item) => !item.enabled) && (
          <div className="space-y-1 pt-2">
            <p className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.disabledGroup")}</p>
            {imageListed.filter((item) => !item.enabled).map((item) => (
              <label key={item.modelId} className="flex items-center gap-2 text-sm" data-model-id={item.modelId}>
                <input
                  type="checkbox"
                  checked={false}
                  aria-label={`image-candidate-${item.modelId}`}
                  onChange={(e) => toggleImageModel(item.modelId, e.target.checked)}
                  className="rounded border-dosia-border"
                />
                <span>{formatProtocolLabel(item.modelId, item.protocol)}</span>
              </label>
            ))}
          </div>
        )}
      </ToolCard>

      <ToolCard
        title={t("admin.toolCuration.card.videoGen.title")}
        tool={t("admin.toolCuration.card.videoGen.tool")}
        blurb={t("admin.toolCuration.card.videoGen.blurb")}
        enabled={enabledVideoCount}
        total={videoGenCandidateRefs.length}
      >
        <p className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.enabledGroup")}</p>
        <div className="space-y-3">
          {videoListed.filter((item) => item.enabled).map((item) => {
            const index = videoDisplayRows.findIndex((row) => row.modelId === item.modelId);
            const row = index >= 0 ? videoDisplayRows[index] : undefined;
            if (!row || index < 0) {
              return (
                <label key={item.modelId} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked onChange={(e) => toggleVideoModel(item.modelId, e.target.checked)} />
                  <span>{formatProtocolLabel(item.modelId, item.protocol)}</span>
                </label>
              );
            }
            const flags = videoFlagsById.get(item.modelId) ?? videoGenFlagsFromIndex(catalogIndex, item.modelId);
            const schema = row.paramSchema ?? getGrokMediaModelContract(row.modelId)?.paramSchema ?? {};
            const modes = schema.mode ?? [];
            const requestedMode = videoModeByRow[row.uid];
            const activeMode = requestedMode && modes.includes(requestedMode) ? requestedMode : modes[0];
            const editableParams = activeMode === "extension"
              ? ["extension_duration"]
              : activeMode === "edit" || !activeMode
                ? []
                : ["aspect_ratio", "resolution", "duration"];
            const inputTypes = schema.input_type ?? ["url", "data_url", "file_id"];
            const rowViolations = videoViolationsByRow.get(index) ?? [];
            const modeRule = activeMode === "text_to_video"
              ? t("admin.toolCuration.videoRule.text")
              : activeMode === "image_to_video"
                ? t("admin.toolCuration.videoRule.image", { types: inputTypes.join(" / ") })
                : activeMode === "reference_to_video"
                  ? t("admin.toolCuration.videoRule.reference", { types: inputTypes.join(" / ") })
                  : activeMode === "edit"
                    ? t("admin.toolCuration.videoRule.edit")
                    : activeMode === "extension"
                      ? t("admin.toolCuration.videoRule.extend")
                      : t("admin.toolCuration.videoRule.empty");
            return (
              <div key={row.uid} className="space-y-2 rounded border border-dosia-border/60 p-3" data-model-id={item.modelId}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="checkbox"
                    checked
                    onChange={(e) => toggleVideoModel(item.modelId, e.target.checked)}
                    className="rounded border-dosia-border"
                  />
                  <input
                    readOnly
                    aria-label={`video-model-${index}`}
                    value={row.modelId}
                    className="min-w-[12rem] flex-1 rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm"
                  />
                  <span className="text-xs text-dosia-text-secondary">{item.protocol ? `· ${item.protocol}` : ""}</span>
                  <CapabilityChip ok={flags.generate} label={t("admin.toolCuration.card.videoGen.capability.generate")} />
                  <CapabilityChip ok={flags.edit} label={t("admin.toolCuration.card.videoGen.capability.edit")} />
                  <CapabilityChip ok={flags.extend} label={t("admin.toolCuration.card.videoGen.capability.extend")} />
                  <WarningBadge item={item} ineligibleText={ineligibleText(item.rejection, false)} />
                  <IsolatedDraftInput
                    initial={row.group}
                    aria-label={`video-group-${index}`}
                    placeholder={t("admin.toolCuration.group.short")}
                    onDraft={(next) => {
                      setVideoGenTouched(true);
                      const base = videoGenRef.current.length > 0 ? videoGenRef.current : takeVideoGen([]);
                      if (videoGenRef.current.length === 0) {
                        videoGenRef.current = base;
                        updateVideoGen(() => base);
                      }
                      const mapped = (videoGenRef.current.length > 0 ? videoGenRef.current : base).map((entry, itemIndex) =>
                        itemIndex === index ? { ...entry, group: next } : entry);
                      videoGenRef.current = mapped;
                    }}
                    className="w-40"
                  />
                  <IsolatedDraftInput
                    initial={row.scenario}
                    aria-label={`video-scenario-${index}`}
                    placeholder={t("admin.toolCuration.scenario.short")}
                    onDraft={(next) => {
                      setVideoGenTouched(true);
                      const base = videoGenRef.current.length > 0 ? videoGenRef.current : takeVideoGen([]);
                      if (videoGenRef.current.length === 0) {
                        videoGenRef.current = base;
                        updateVideoGen(() => base);
                      }
                      const mapped = (videoGenRef.current.length > 0 ? videoGenRef.current : base).map((entry, itemIndex) =>
                        itemIndex === index ? { ...entry, scenario: next } : entry);
                      videoGenRef.current = mapped;
                    }}
                    className="w-40"
                  />
                  <button type="button" aria-label={`video-上移-${index}`} onClick={() => moveVideoModel(index, -1)} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↑</button>
                  <button type="button" aria-label={`video-下移-${index}`} onClick={() => moveVideoModel(index, 1)} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↓</button>
                  <button type="button" aria-label={`video-删除-${index}`} onClick={() => toggleVideoModel(item.modelId, false)} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover text-dosia-danger">✕</button>
                </div>
                {rowViolations.map((violation, violationIndex) => (
                  <p key={violationIndex} role="alert" className="text-xs text-dosia-danger">{violation.message}</p>
                ))}
                {modes.length > 0 && (
                  <div
                    className="space-y-3 rounded border border-dosia-border/60 bg-dosia-bg-light p-3"
                    data-testid={`video-contract-${index}`}
                  >
                    <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={`视频生成模式-${index}`}>
                      {modes.map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          role="tab"
                          aria-selected={activeMode === mode}
                          onClick={() => setVideoModeByRow((current) => ({ ...current, [row.uid]: mode }))}
                          className={`rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                            activeMode === mode
                              ? "border-dosia-accent bg-dosia-accent/10 text-dosia-accent"
                              : "border-dosia-border bg-dosia-bg text-dosia-text-secondary hover:bg-dosia-hover"
                          }`}
                        >
                          {VIDEO_MODE_LABELS[mode] ?? mode}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-dosia-text-secondary" data-testid={`video-mode-rule-${index}`}>
                      {modeRule}
                    </p>
                    {editableParams.length > 0 && (
                      <div className="grid gap-2 sm:grid-cols-3">
                        {editableParams.map((param) => {
                          const allowed = schema[param] ?? [];
                          if (allowed.length === 0) return null;
                          const label = param === "aspect_ratio"
                            ? t("admin.toolCuration.videoParam.aspect")
                            : param === "resolution"
                              ? t("admin.toolCuration.videoParam.resolution")
                              : param === "extension_duration"
                                ? t("admin.toolCuration.videoParam.extend")
                                : t("admin.toolCuration.videoParam.duration");
                          return (
                            <label key={param} className="space-y-1 text-xs text-dosia-text-secondary">
                              <span>{label}</span>
                              <Select
                                value={row.paramDefaults?.[param] ?? ""}
                                aria-label={`video-default-${param}-${index}`}
                                onChange={(event) => {
                                  setVideoGenTouched(true);
                                  updateVideoGen((current) => takeVideoGen(current).map((entry, itemIndex) => {
                                    if (itemIndex !== index) return entry;
                                    const defaults = { ...(entry.paramDefaults ?? {}) };
                                    if (event.target.value) defaults[param] = event.target.value;
                                    else delete defaults[param];
                                    return { ...entry, paramDefaults: Object.keys(defaults).length ? defaults : undefined };
                                  }));
                                }}
                              >
                                <option value="">{t("admin.toolCuration.params.followGateway")}</option>
                                {allowed.map((value) => <option key={value} value={value}>{value}</option>)}
                              </Select>
                              <span className="block text-[10px] text-dosia-text-dim">{allowed.join(" / ")}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {videoListed.some((item) => !item.enabled) && (
          <div className="space-y-1 pt-2">
            <p className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.disabledGroup")}</p>
            {videoListed.filter((item) => !item.enabled).map((item) => (
              <label key={item.modelId} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={false}
                  aria-label={`video-candidate-${item.modelId}`}
                  onChange={(e) => toggleVideoModel(item.modelId, e.target.checked)}
                  className="rounded border-dosia-border"
                />
                <span>{formatProtocolLabel(item.modelId, item.protocol)}</span>
              </label>
            ))}
          </div>
        )}
      </ToolCard>

      <ProviderChainCard<WebSearchProviderId>
        title={t("admin.toolCuration.card.webSearch.title")}
        tool="web_search"
        blurb={t("admin.toolCuration.card.webSearch.blurb")}
        listed={webSearchListed}
        touched={webSearchTouched}
        setTouched={setWebSearchTouched}
        rowsRef={webSearchRef}
        setRows={setWebSearchProviders}
        optionLabels={WEB_SEARCH_PROVIDER_OPTION_LABELS}
        groupPlaceholder={WEB_SEARCH_GROUP_PLACEHOLDER}
        groupHint={t("admin.toolCuration.card.webSearch.groupHint")}
        ariaPrefix="web-search"
        moveUpLabel="搜索链上移"
        moveDownLabel="搜索链下移"
      />

      <ProviderChainCard<AudioTranscribeProviderId>
        title={t("admin.toolCuration.card.audioTranscribe.title")}
        tool="audio_transcribe"
        blurb={t("admin.toolCuration.card.audioTranscribe.blurb")}
        listed={audioTranscribeListed}
        touched={audioTranscribeTouched}
        setTouched={setAudioTranscribeTouched}
        rowsRef={audioTranscribeRef}
        setRows={setAudioTranscribeProviders}
        optionLabels={AUDIO_TRANSCRIBE_PROVIDER_OPTION_LABELS}
        groupPlaceholder={AUDIO_TRANSCRIBE_GROUP_PLACEHOLDER}
        groupHint={t("admin.toolCuration.card.audioTranscribe.groupHint")}
        ariaPrefix="audio-transcribe"
        moveUpLabel="转写链上移"
        moveDownLabel="转写链下移"
      />

      <section className="space-y-2 rounded border border-dosia-border/60 p-3">
        <div>
          <h2 className="text-sm font-medium text-dosia-text">{t("admin.toolCuration.card.xSearch.title")}</h2>
          <p className="mt-1 text-xs text-dosia-text-secondary">
            {t("admin.toolCuration.card.xSearch.blurb")}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-dosia-text">
          <input
            type="checkbox"
            checked={xSearchEnabled}
            onChange={(e) => setXSearchEnabled(e.target.checked)}
            className="rounded border-dosia-border"
          />
          {t("admin.toolCuration.card.xSearch.enable")}
        </label>
        {xSearchEnabled && (
          <>
            <IsolatedDraftInput
              initial={xSearchGroup}
              aria-label="x-search-group"
              placeholder={t("admin.toolCuration.card.xSearch.groupPlaceholder")}
              onDraft={(next) => {
                xSearchGroupRef.current = next;
              }}
              className="max-w-md"
            />
            {!xSearchGroup.trim() && (
              <p className="text-xs text-dosia-warning">{t("admin.toolCuration.card.xSearch.emptyGroup")}</p>
            )}
          </>
        )}
      </section>

      <ToolCard
        title={t("admin.toolCuration.card.work.title")}
        tool={t("admin.toolCuration.card.work.tool")}
        blurb={t("admin.toolCuration.card.work.blurb")}
        enabled={enabledWorkCount}
        total={workCandidateRefs.length}
      >
        <ModelPickList
          items={workListed}
          ariaPrefix="workmodel"
          sortable={false}
          onToggle={toggleWorkRow}
          ineligibleKind="work"
          renderExtra={(item, i) => {
            const base = baseId(item.modelId.trim());
            const entry = base ? capabilityByBase.get(base) : undefined;
            const thinkingParam = entry?.capabilities?.thinkingParam;
            const reasoningLevels = thinkingParam?.supported === true
              ? (thinkingParam.levels ?? []).filter((level): level is EffortLevel => (
                level === "low" || level === "medium" || level === "high" || level === "xhigh" || level === "max"
              ))
              : [];
            const outputTokenParam = entry?.capabilities?.outputTokenParam;
            const hasParamControls = reasoningLevels.length > 0 || outputTokenParam !== undefined;
            const rowOpen = openParamRow === item.modelId;
            if (!item.enabled || !base || !hasParamControls) return null;
            return (
              <div className="ml-6 space-y-1">
                <button
                  type="button"
                  onClick={() => setOpenParamRow((cur) => (cur === item.modelId ? null : item.modelId))}
                  className="text-xs font-medium text-dosia-accent hover:underline"
                >
                  {rowOpen ? t("admin.toolCuration.work.collapseParams") : t("admin.toolCuration.work.openParams")}
                </button>
                {rowOpen && (
                  <div className="space-y-1 rounded border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs">
                    {reasoningLevels.length > 0 && (
                      <label className="flex items-center gap-2">
                        <span className="text-dosia-text-secondary">{t("admin.toolCuration.work.effort")}</span>
                        <Select
                          value={modelParams[base]?.reasoningEffort ?? ""}
                          onChange={(e) => setModelParam(base, {
                            reasoningEffort: (
                              e.target.value === "low" || e.target.value === "medium" || e.target.value === "high"
                              || e.target.value === "xhigh" || e.target.value === "max"
                            ) ? e.target.value : undefined,
                          })}
                          aria-label={`推理档位-${base}`}
                        >
                          <option value="">{t("admin.toolCuration.work.effortFollow", { def: thinkingParam?.default ? `（${thinkingParam.default}）` : "" })}</option>
                          {reasoningLevels.map((lvl) => <option key={lvl} value={lvl}>{lvl}</option>)}
                        </Select>
                      </label>
                    )}
                    {outputTokenParam && (
                      <label className="flex items-center gap-2">
                        <span className="text-dosia-text-secondary">
                          {t("admin.toolCuration.work.maxTokens", {
                            def: outputTokenParam.default ?? t("admin.toolCuration.params.followGateway"),
                            max: outputTokenParam.max !== undefined ? ` / ${outputTokenParam.max}` : "",
                          })}
                        </span>
                        <input
                          type="number"
                          min={1}
                          max={outputTokenParam.max}
                          placeholder={t("admin.toolCuration.work.maxTokensPlaceholder")}
                          value={modelParams[base]?.maxTokens ?? ""}
                          onChange={(e) => setModelParam(base, {
                            maxTokens: e.target.value ? Number(e.target.value) : undefined,
                          })}
                          className="w-32 rounded border border-dosia-border bg-dosia-bg px-2 py-1"
                          aria-label={`最大输出 token-${base}`}
                        />
                      </label>
                    )}
                    <p className="text-dosia-text-dim">{t("admin.toolCuration.work.paramsHint")}</p>
                  </div>
                )}
              </div>
            );
          }}
        />
      </ToolCard>

      <div className="flex items-center gap-3 border-t border-dosia-border pt-4">
        <PrimaryButton onClick={save} disabled={saving || hasBlockingViolations}>
          {saving ? t("admin.toolCuration.saving") : t("admin.toolCuration.save")}
        </PrimaryButton>
        <span className="text-xs text-dosia-text-secondary">
          {hasBlockingViolations ? (
            <span className="text-dosia-danger">
              {t("admin.toolCuration.blockingViolations", { count: imageGenViolations.length + videoGenViolations.length })}
            </span>
          ) : (
            <>
              {configuredCount > 0
                ? t("admin.toolCuration.configuredCount", { count: configuredCount })
                : t("admin.toolCuration.allEmpty")}
              {savedAt && t("admin.toolCuration.savedAt", { time: savedAt })}
            </>
          )}
        </span>
      </div>
      </>
      )}
    </div>
  );
}


function WarningBadge(props: { item: ListedModel; ineligibleText: string }): JSX.Element | null {
  const { t } = useTranslation();
  if (props.item.warning === "retired") {
    return (
      <span className="text-xs text-dosia-danger" data-warning="retired">
        {t("admin.toolCuration.retired")}
        <span className="ml-1 text-dosia-text-secondary">{t("admin.toolCuration.retiredHint")}</span>
      </span>
    );
  }
  if (props.item.warning === "ineligible") {
    return (
      <span className="text-xs text-dosia-warning" data-warning="ineligible">
        {t("admin.toolCuration.ineligible")}
        <span className="ml-1">{props.ineligibleText}</span>
      </span>
    );
  }
  return null;
}

function CapabilityChip(props: { ok: boolean; label: string }): JSX.Element {
  return (
    <span className={cn("text-xs", props.ok ? "text-dosia-text" : "text-dosia-text-dim")}>
      {props.label}{props.ok ? "" : " ✕"}
    </span>
  );
}

const ModelPickList = React.memo(function ModelPickList(props: {
  items: ListedModel[];
  ariaPrefix: string;
  sortable: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onMove?: (enabledIndex: number, dir: -1 | 1) => void;
  scenarioPlaceholder?: string;
  scenarioById?: Readonly<Record<string, string>>;
  onScenario?: (id: string, scenario: string) => void;
  ineligibleKind: "work" | "other";
  renderExtra?: (item: ListedModel, index: number) => React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const enabled = props.items.filter((item) => item.enabled);
  const disabled = props.items.filter((item) => !item.enabled);
  let running = 0;
  const renderRow = (item: ListedModel, group: "enabled" | "disabled"): JSX.Element => {
    const index = running;
    running += 1;
    const enabledIndex = enabled.findIndex((row) => row.modelId === item.modelId);
    const ineligible = item.rejection === "generation-endpoint" && props.ineligibleKind === "work"
      ? t("admin.toolCuration.reason.generation-endpoint-work")
      : item.rejection === "generation-endpoint"
        ? t("admin.toolCuration.reason.generation-endpoint")
        : item.rejection === "capability-missing"
          ? t("admin.toolCuration.reason.capability-missing")
          : item.rejection === "no-speakable-leg"
            ? t("admin.toolCuration.reason.no-speakable-leg")
            : t("admin.toolCuration.reason.not-in-catalog");
    return (
      <div key={`${group}-${item.modelId}`} className="space-y-1" data-model-id={item.modelId}>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="checkbox"
            checked={item.enabled}
            aria-label={`${props.ariaPrefix}-${index}`}
            onChange={(e) => props.onToggle(item.modelId, e.target.checked)}
            className="rounded border-dosia-border"
          />
          <span className="text-sm text-dosia-text" data-testid={`protocol-${props.ariaPrefix}-${item.modelId}`}>
            {formatProtocolLabel(item.modelId, item.protocol)}
          </span>
          <WarningBadge item={item} ineligibleText={ineligible} />
          {item.enabled && props.scenarioById && props.onScenario && (
            <IsolatedDraftInput
              initial={props.scenarioById[item.modelId] ?? ""}
              placeholder={props.scenarioPlaceholder}
              onDraft={(next) => props.onScenario?.(item.modelId, next)}
              className="flex-[3] min-w-[10rem]"
            />
          )}
          {item.enabled && props.sortable && props.onMove && (
            <span className="inline-flex gap-0.5">
              <button
                type="button"
                aria-label={`${props.ariaPrefix}-上移-${enabledIndex}`}
                onClick={() => props.onMove?.(enabledIndex, -1)}
                className="px-1.5 py-0.5 rounded hover:bg-dosia-hover"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`${props.ariaPrefix}-下移-${enabledIndex}`}
                onClick={() => props.onMove?.(enabledIndex, 1)}
                className="px-1.5 py-0.5 rounded hover:bg-dosia-hover"
              >
                ↓
              </button>
            </span>
          )}
        </div>
        {props.renderExtra?.(item, index)}
      </div>
    );
  };
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-dosia-text">
        {props.sortable ? t("admin.toolCuration.enabledGroup") : t("admin.toolCuration.enabledNoPriority")}
      </p>
      {enabled.map((item) => renderRow(item, "enabled"))}
      {disabled.length > 0 && (
        <>
          <p className="text-xs font-medium text-dosia-text pt-1">{t("admin.toolCuration.disabledGroup")}</p>
          {disabled.map((item) => renderRow(item, "disabled"))}
        </>
      )}
    </div>
  );
});

function RowControls(props: { onUp: () => void; onDown: () => void; onRemove: () => void }): JSX.Element {
  return (
    <div className="flex items-center gap-1 text-dosia-text-secondary">
      <button type="button" aria-label="上移" onClick={props.onUp} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↑</button>
      <button type="button" aria-label="下移" onClick={props.onDown} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover">↓</button>
      <button type="button" aria-label="删除" onClick={props.onRemove} className="px-1.5 py-0.5 rounded hover:bg-dosia-hover text-dosia-danger">✕</button>
    </div>
  );
}
